/**
 * Material Generation Tools
 *
 * `generate_material` synthesizes a PBR material from a description — albedo,
 * a generated normal map and the metallic/roughness scalars, packed as a GLB —
 * and optionally applies it to a scene object's material slots.
 *
 * The pipeline runs entirely server-side (gateway role `material`): the
 * gateway holds the provider key, bills the team, and decides which models
 * answer. Nothing here names one.
 *
 * It is the *surface* counterpart to generate_model. A prop that needs its own
 * geometry goes to generate_model; an existing surface that needs to look like
 * something goes here, which is far cheaper and keeps the mesh you already
 * have. Prefer the catalog's authored materials when one fits — this is for
 * surfaces nothing in the library covers.
 */

import type { ToolDefinition } from './types';
import type { ToolContext } from '../agent-helpers';
import { resolveObject } from '../agent-helpers';
import type { SceneObject } from 'trace-viewer/scene';
import { Renderer, isPropertyBinding } from 'trace-viewer/behaviors';
import {
    AddBehaviorCommand,
    BehaviorCommitCommand,
    MaterialPreviews,
    getUndoManager,
    groupSlotsByMaterial,
    type MaterialGroup,
} from 'trace-viewer/editor';
import { dispatchBehaviorChanged } from '../../editor-events';
import {
    ensureGatewayDiscovery,
    generateMaterial,
    getGatewayRoute,
    hasGatewayCapability,
    isGatewayAvailable,
    isGatewayConfigured,
    type GeneratedMaterialProperties,
    type MaterialGenRequest,
} from '../gateway';
import { applyMaterialSrcOverride } from './material-override';
import { persistGeneratedBinary } from './persist-generated';

/** What the tool timeline shows in place of a model id. */
function materialLabel(): string {
    return getGatewayRoute('material')?.label ?? 'Trace AI · material';
}

/** The slots one generated material should be written to, or why it can't be. */
type SlotPlan =
    | { slotIndices: number[]; description: string }
    | { error: string };

/**
 * Decide which mesh slots the material lands on.
 *
 * Grouped by authored material name rather than taken slot-by-slot:
 * `targetMaterialIndex` is a MESH slot index, so one glTF material shared by
 * several meshes shows up as several slots and a single choice is several
 * writes. Naming a slot therefore swaps its whole material, not one mesh of it.
 *
 * With no slot named, every group is written *except* unlit ones — flat
 * authored content (a text label, an image plane, a baked UI panel) whose look
 * IS its texture. Style adoption skips those for the same reason: re-materialing
 * them destroys the content rather than theming it.
 */
function planSlots(target: SceneObject, materialIndex: number | undefined): SlotPlan {
    const slots = MaterialPreviews.detectMaterialSlots(target);
    if (slots.length === 0) {
        return {
            error: `"${target.name}" has no material slots — it is not a mesh, or its model has not finished loading. `
                + 'Generate without `object` and apply the returned src once it has.',
        };
    }
    const groups = groupSlotsByMaterial(slots);

    if (materialIndex !== undefined) {
        const group = groups.find(g => g.slotIndices.includes(materialIndex));
        if (!group) {
            const inventory = groups
                .map(g => `${g.materialName} (slots ${g.slotIndices.join(', ')})`)
                .join('; ');
            return { error: `"${target.name}" has no material slot ${materialIndex}. Slots: ${inventory}.` };
        }
        return {
            slotIndices: group.slotIndices,
            description: `"${group.materialName}" (slot${group.slotIndices.length > 1 ? 's' : ''} ${group.slotIndices.join(', ')})`,
        };
    }

    const paintable: MaterialGroup[] = groups.filter(g => !g.unlit);
    if (paintable.length === 0) {
        return {
            error: `Every material on "${target.name}" is flat authored content (unlit) — a label, image or baked panel. `
                + 'Re-materialing those destroys the content. Name a `material_index` explicitly to override this.',
        };
    }
    const skipped = groups.length - paintable.length;
    return {
        slotIndices: paintable.flatMap(g => g.slotIndices),
        description: paintable.length === 1
            ? `"${paintable[0].materialName}" (slot${paintable[0].slotIndices.length > 1 ? 's' : ''} ${paintable[0].slotIndices.join(', ')})`
            : `all ${paintable.length} materials${skipped > 0 ? ` (${skipped} unlit slot${skipped > 1 ? 's' : ''} left alone)` : ''}`,
    };
}

/**
 * The chosen slots whose `src` is currently a style binding (`$material`…)
 * rather than a literal.
 *
 * Writing a literal over one is what the caller asked for — "make this surface
 * look like X" is about this object, not the theme every object shares — but it
 * takes the slot out of the style, so the next style swap will no longer move
 * it. Silent is the wrong way to do that, so the result says so.
 */
function boundSlots(target: SceneObject, slotIndices: readonly number[]): number[] {
    const materials = target.getBehavior(Renderer)?.getData().materials ?? [];
    return slotIndices.filter(index => {
        const entry = materials.find(m => (m.targetMaterialIndex ?? 0) === index);
        return entry?.src !== undefined && isPropertyBinding(entry.src);
    });
}

/** Write the material src onto `target`'s Renderer, with undo. Returns an error string on failure. */
async function applyToRenderer(
    target: SceneObject,
    slotIndices: number[],
    src: string,
): Promise<string | null> {
    const existingRenderer = target.getBehavior(Renderer);
    const before = existingRenderer?.getData() ?? null;
    const materials = applyMaterialSrcOverride(before?.materials, slotIndices, src);

    try {
        const renderer = await target.setBehavior(Renderer, { materials });

        if (!getUndoManager().isUndoRedoing) {
            getUndoManager().push(before
                ? new BehaviorCommitCommand(target, 'Renderer', before, renderer.getData())
                : new AddBehaviorCommand(target, 'Renderer', renderer.getData()));
        }

        dispatchBehaviorChanged({
            objectPath: target.getPath(),
            behaviorType: 'Renderer',
            action: before ? 'updated' : 'added',
        });
        return null;
    } catch (err) {
        return err instanceof Error ? err.message : String(err);
    }
}

/** "metallic 1, roughness 0.75, normal 2.5" — what the pipeline estimated. */
function describeProperties(properties: GeneratedMaterialProperties | null): string {
    if (!properties) return '';
    const parts = [
        `metallic ${properties.metallic}`,
        `roughness ${properties.roughness}`,
        properties.normalStrength > 0 ? `normal ${properties.normalStrength}` : 'flat (no normal map)',
    ];
    return ` — ${parts.join(', ')}`;
}

async function executeGenerateMaterial(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const prompt = (input.prompt as string | undefined)?.trim();
    const objectRef = input.object as string | undefined;
    const materialIndex = input.material_index as number | undefined;
    const quality = input.quality as MaterialGenRequest['quality'];
    const resolution = input.resolution as MaterialGenRequest['resolution'];

    if (!prompt) {
        return 'Error: generate_material needs a prompt describing the surface.';
    }
    if (!isGatewayConfigured()) {
        return 'Error: material generation requires the Trace AI gateway, which this build has no URL for.';
    }
    // The routing table arrives asynchronously and a tool can run the moment
    // the editor loads, so reading it early answers "no route" for a gateway
    // that serves the role perfectly well.
    await ensureGatewayDiscovery();
    if (!hasGatewayCapability('material')) {
        // Two very different failures used to share one message. Separating
        // them matters: one is fixed by signing in, the other by updating the
        // gateway, and telling a signed-in user to sign in sends them nowhere.
        return isGatewayAvailable()
            ? 'Error: this gateway serves no `material` route. Its build predates material generation '
                + '(ml-services #27) — restart or redeploy the gateway, then reload the editor so it '
                + 're-reads /v1/routing.'
            : 'Error: material generation runs server-side and needs a Trace sign-in. Sign in to the catalog, or switch the AI source to Trace AI in settings.';
    }

    // Resolve the target and its slots BEFORE generating: a material costs two
    // paid image calls, and discovering "no such object" afterwards spends them
    // for nothing.
    let target: SceneObject | null = null;
    let plan: SlotPlan | null = null;
    if (objectRef) {
        target = resolveObject(objectRef, ctx);
        if (!target) {
            return objectRef === 'selected'
                ? 'Error: nothing is selected. Select the object to material, or pass its name as `object`.'
                : `Error: Object "${objectRef}" not found.`;
        }
        plan = planSlots(target, materialIndex);
        if ('error' in plan) return `Error: ${plan.error}`;
    }

    console.log(`[AI] generate_material: "${prompt.slice(0, 100)}"${objectRef ? ` → ${objectRef}` : ''}`);
    ctx.onToolProgress?.('generating material…');
    const result = await generateMaterial({
        prompt,
        ...(quality ? { quality } : {}),
        ...(resolution ? { resolution } : {}),
    });
    if ('error' in result) {
        console.error('[AI] generate_material error:', result.error);
        return `Error generating material: ${result.error}`;
    }

    const { blob, properties, resolution: madeAt, elapsedMs } = result;
    const seconds = Math.round(elapsedMs / 1000);
    const sizeKb = Math.round(blob.size / 1024);

    let src: string;
    try {
        src = await persistGeneratedBinary(ctx, blob, 'glb');
    } catch (err) {
        return `Error storing the generated material: ${err instanceof Error ? err.message : String(err)}`;
    }

    const made = `Generated material "${prompt.slice(0, 60)}" (${madeAt ?? 512}px, ${sizeKb} KB, ${seconds}s)`
        + `${describeProperties(properties)}.`;

    if (!target || !plan || 'error' in plan) {
        return `${made} Stored as \`${src}\` — apply it by committing a Renderer material with that \`src\` `
            + 'on the slot you want, or call generate_material again with `object` to have it applied for you.';
    }

    // Read before the write, while the bindings are still there to see.
    const unbound = boundSlots(target, plan.slotIndices);

    const failure = await applyToRenderer(target, plan.slotIndices, src);
    if (failure) return `${made} Stored as \`${src}\`, but applying it to "${target.name}" failed: ${failure}`;

    ctx.traceScene.select(target);
    ctx.onSceneChanged?.();
    console.log(`[AI] generate_material: applied ${src} to "${target.name}" slots ${plan.slotIndices.join(', ')}`);

    const styleNote = unbound.length > 0
        ? `Slot${unbound.length > 1 ? 's' : ''} ${unbound.join(', ')} followed the scene's style — `
            + 'the generated material now overrides that, so a later style change will not move it. '
        : '';

    return `${made} Applied to "${target.name}" → ${plan.description}. ${styleNote}`
        + 'The material tiles across the surface\'s existing UVs — if it reads too large or too small, '
        + 'adjust the slot\'s `texture_transform.scale` on the Renderer rather than regenerating. '
        + 'Runtime-only until save_scene.';
}

export const materialTools: ToolDefinition[] = [
    {
        tool: {
            name: 'generate_material',
            input_schema: {
                type: 'object',
                properties: {
                    prompt: { type: 'string' },
                    object: { type: 'string' },
                    material_index: { type: 'number' },
                    quality: {
                        type: 'string',
                        enum: ['draft', 'standard', 'high'],
                    },
                    resolution: {
                        type: 'number',
                        enum: [512, 1024],
                    },
                },
                required: ['prompt'],
            },
        },
        execute: executeGenerateMaterial,
        modelLabel: materialLabel,
    },
];
